import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import api from 'api';
import useCallbackRef from 'hooks/useCallbackRef';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ManageFileAccess from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/DataRoomDocumentDrawers/ManageFileAccess';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { DataRoom } from 'router/subrouters/DataRoom/models/DataRoom';
import { DataRoomMembers } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { getIsBuySide, getIsSellSide } from 'router/subrouters/DataRoom/utils';
import { getHasElementsInArray, getIsArrayEmpty } from 'utils';

export type DataRoomFileAccessRef = {
  open: (nodes: DataRoomNode[]) => void;
};

type DataRoomFileAccessProps = {
  dataRoomId: string;
  /**
   * Load the room and its members ahead of the first `open`, so the drawer has
   * everything it needs the moment the user asks for it.
   */
  prefetch?: boolean;
  scrollToBuySide?: boolean;
};

/**
 * The Data Room's file access drawer, usable from anywhere in the app.
 *
 * `ManageFileAccess` reads the room's access confirmation settings, the current user's
 * side and the member list off the Data Room providers, and talks to the Data Room
 * client. Neither is available outside the Data Room route — and `DataRoomProvider` is
 * far too heavy to mount elsewhere (it owns the page loader, its own banners and an
 * idle-timeout prompt). So this component fetches those few values over the platform
 * client and supplies them through the very same contexts, which leaves the drawer and
 * everything below it untouched.
 *
 * Inside the Data Room the real providers are already in scope, so it steps aside.
 */
const DataRoomFileAccess: React.ForwardRefRenderFunction<
  DataRoomFileAccessRef,
  DataRoomFileAccessProps
> = (props, ref) => {
  const { dataRoomId, prefetch, scrollToBuySide } = props;

  const [nodes, setNodes] = useState<DataRoomNode[]>([]);
  const [room, setRoom] = useState<DataRoom>();
  const [members, setMembers] = useState<DataRoomMembers>();

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const { popServerError } = useContext(PopNotificationsContext);

  const inheritedDataRoom = useContext(DataRoomContext);
  const inheritedMembers = useContext(MembersContext);

  const isInsideDataRoom =
    !!dataRoomId && inheritedDataRoom.dataRoom?._id === dataRoomId;

  const open = useCallback((nodes: DataRoomNode[]) => setNodes(nodes), []);

  useImperativeHandle(ref, () => ({ open }), [open]);

  const shouldFetch =
    !isInsideDataRoom && (prefetch || getHasElementsInArray(nodes));

  useEffect(() => {
    if (!shouldFetch || !dataRoomId) return;

    (async () => {
      try {
        const [{ data: room }, { data: members }] = await Promise.all([
          api.dataRoom.room.getRoom(dataRoomId),
          api.dataRoom.members.getMembers(dataRoomId),
        ]);

        setRoom(room);
        setMembers(members);
      } catch (e) {
        popServerError(e);
      }
    })();
  }, [shouldFetch, dataRoomId, popServerError]);

  const isReady = isInsideDataRoom || (!!room && !!members);

  // The drawer is only mounted once its data is in, so an `open` that lands first
  // waits here for it. `SideDrawerRef` is a new object on every open/close, so without
  // the marker closing the drawer would immediately reopen it.
  const openedFor = useRef<DataRoomNode[]>();

  useEffect(() => {
    if (!drawer || getIsArrayEmpty(nodes) || openedFor.current === nodes)
      return;

    openedFor.current = nodes;
    drawer.setOpen(true);
  }, [drawer, nodes]);

  const qaSide = room?.currentMember?.qaSide;

  const dataRoomValue = useMemo(
    () => ({
      ...inheritedDataRoom,
      dataRoom: room,
      qaSide,
      isSellSide: getIsSellSide(qaSide),
      isBuySide: getIsBuySide(qaSide),
    }),
    [inheritedDataRoom, room, qaSide],
  );

  const membersValue = useMemo(
    () => ({ ...inheritedMembers, ...members }),
    [inheritedMembers, members],
  );

  if (!dataRoomId || !isReady) return null;

  if (isInsideDataRoom)
    return (
      <ManageFileAccess
        ref={drawerRef}
        nodes={nodes}
        parentId={nodes[0]?.parentId}
        scrollToBuySide={scrollToBuySide}
      />
    );

  return (
    <DataRoomContext.Provider value={dataRoomValue}>
      <MembersContext.Provider value={membersValue}>
        <ManageFileAccess
          ref={drawerRef}
          nodes={nodes}
          parentId={nodes[0]?.parentId}
          api={api.dataRoom.nodes}
          scrollToBuySide={scrollToBuySide}
        />
      </MembersContext.Provider>
    </DataRoomContext.Provider>
  );
};

export default forwardRef(DataRoomFileAccess);
